import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout, QrCode, ShieldCheck, Zap, ArrowRight, Smartphone, Sparkles, Gift, Clock, History, ShoppingBag, Award, BarChart3, TrendingUp, Users, MousePointer2, Star, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import { shopService } from '../../services/shopService';
import Swal from 'sweetalert2';

const Home = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const userPhone = sessionStorage.getItem('userPhone');
  const userName = sessionStorage.getItem('userName');

  const [currentShop, setCurrentShop] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    ownerName: '',
    shopName: '',
    phoneNumber: '',
    businessType: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!formData.ownerName || !formData.shopName || !formData.phoneNumber) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please fill in Name, Shop Name, and Phone Number.',
        confirmButtonColor: '#4f46e5'
      });
      return;
    }

    setSubmitting(true);
    try {
      await shopService.addInquiry(formData);
      Swal.fire({
        icon: 'success',
        title: 'Inquiry Sent!',
        text: 'Our team will contact you shortly.',
        confirmButtonColor: '#4f46e5',
        timer: 3000,
        showConfirmButton: false
      });
      setFormData({
        ownerName: '',
        shopName: '',
        phoneNumber: '',
        businessType: '',
        message: ''
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: 'Could not send inquiry. Please try again.',
        confirmButtonColor: '#4f46e5'
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchCurrentShop = async () => {
      const shopId = sessionStorage.getItem('currentShopId');
      if (shopId) {
        try {
          const shopData = await shopService.getShopById(shopId);
          setCurrentShop(shopData);
        } catch (error) {
          console.error("Error fetching current shop:", error);
        }
      }
    };
    fetchCurrentShop();

    if (userPhone) {
      const fetchHistory = async () => {
        setLoading(true);
        try {
          const data = await userService.getUserScratches(userPhone);
          setHistory(data);
        } catch (error) {
          console.error("Error fetching history:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchHistory();
    }
  }, [userPhone]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <QrCode className="text-brand-primary" />,
      title: "Scan QR",
      description: "Quickly scan the shop's QR code to unlock your digital rewards."
    },
    {
      icon: <Sparkles className="text-brand-primary" />,
      title: "Scratch & Win",
      description: "Revealing prizes is fun and instant. No physical cards needed."
    },
    {
      icon: <Gift className="text-brand-primary" />,
      title: "Claim Instant",
      description: "Redeem your won gifts directly at the shop counter."
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans selection:bg-brand-primary/20 selection:text-indigo-900">
      
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 px-6 py-4 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-200 py-3' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <img src="/Sahyadri Tech Logo.png2.png" alt="Sahyadri Tech" className="h-10 w-auto object-contain" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('features')} className="text-sm font-semibold text-slate-500 hover:text-brand-primary transition-colors">How it works</button>
            <button onClick={() => scrollToSection('merchants')} className="text-sm font-semibold text-slate-500 hover:text-brand-primary transition-colors">For Merchants</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative pt-32 md:pt-48 pb-24 px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 dot-pattern opacity-40 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-brand-primary/10 rounded-full blur-[120px] opacity-60 pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1.5 bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider rounded-full mb-6 border border-brand-primary/20">
                Digital Rewards Reimagined
              </span>
              <h1 className="text-5xl md:text-8xl font-bold text-brand-secondary leading-[1.1] tracking-tight mb-8">
                {currentShop ? (
                  <>Unlock Exclusive Rewards at <span className="accent-text">{currentShop.shopName}</span></>
                ) : (
                  <>Win Big with Every <span className="accent-text">Scratch.</span> Anywhere.</>
                )}
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                The modern way to reward loyalty. Just scan, scratch, and celebrate instant prizes at your favorite local shops.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button onClick={() => scrollToSection('contact')} className="modern-button">
                  Grow Your Business
                </button>
              </div>
            </motion.div>
          </div>

          {/* Floating Visual Elements */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mt-20 relative max-w-5xl mx-auto"
          >
            <div className="bg-white p-4 rounded-[2.5rem] shadow-2xl border border-slate-200">
              <img 
                src="https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&q=80&w=1200" 
                alt="App Showcase" 
                className="w-full h-auto rounded-[2rem] object-cover aspect-video md:aspect-[21/9]"
              />
            </div>
            {/* Floating Card UI */}
            <div className="absolute -bottom-10 -right-6 md:right-10 bg-white p-6 rounded-3xl shadow-2xl border border-slate-100 animate-float hidden md:block">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                  <Award className="text-green-600" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Reward Claimed</p>
                  <p className="font-bold text-brand-secondary">Flat 20% OFF Coupon</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white px-6 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-brand-primary font-bold uppercase tracking-widest text-xs mb-3">Simple & Fun</h2>
            <h3 className="text-3xl md:text-5xl font-bold text-brand-secondary">How DigiSahyadri Works</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={i} className="modern-card p-10 group">
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-brand-primary/10 transition-colors">
                  {f.icon}
                </div>
                <h4 className="text-2xl font-bold mb-4">{f.title}</h4>
                <p className="text-slate-500 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History Section - If Logged In */}
      <AnimatePresence>
        {userPhone && (
          <section className="py-24 bg-slate-50 px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                <div>
                  <h2 className="text-brand-primary font-bold uppercase tracking-widest text-xs mb-3">Your Account</h2>
                  <h3 className="text-4xl font-bold text-brand-secondary">Recently Claimed</h3>
                </div>
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Total Rewards Earned</p>
                  <p className="text-3xl font-black text-brand-primary">{history.length}</p>
                </div>
              </div>

              {loading ? (
                <div className="py-20 text-center animate-pulse text-slate-300 font-bold uppercase tracking-[0.2em] text-sm">
                  Fetching your rewards...
                </div>
              ) : history.length === 0 ? (
                <div className="modern-card p-20 text-center">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-200 mx-auto mb-6">
                    <Gift size={40} />
                  </div>
                  <h4 className="text-2xl font-bold mb-2">No Rewards Yet</h4>
                  <p className="text-slate-500 max-w-sm mx-auto">Start scanning QR codes at partner shops to build your reward collection!</p>
                  <button onClick={() => navigate('/scan')} className="mt-8 modern-button">Scan Now</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {history.map((item, idx) => (
                    <motion.div 
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                      className="modern-card p-8 group relative overflow-hidden"
                    >
                      <div className="flex items-center justify-between mb-8">
                        <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-3xl">
                          {item.reward?.icon || '🎁'}
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1 justify-end">
                            <Clock size={12} /> {item.timestamp?.toDate().toLocaleDateString()}
                          </span>
                          <span className="inline-block mt-2 px-3 py-1 bg-green-50 text-green-700 text-[10px] font-bold uppercase rounded-full">Claimed</span>
                        </div>
                      </div>
                      <p className="text-xs font-bold text-brand-primary uppercase tracking-widest mb-2">{item.shopName}</p>
                      <h4 className="text-2xl font-bold">{item.reward?.name}</h4>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </AnimatePresence>

      {/* Merchant CTA Section */}
      <section id="merchants" className="py-24 bg-brand-secondary relative overflow-hidden px-6 scroll-mt-24">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-primary rounded-full blur-[150px] opacity-20 pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h2 className="text-brand-primary font-bold uppercase tracking-widest text-xs mb-6">For Business Owners</h2>
          <h3 className="text-4xl md:text-6xl font-bold text-white mb-8">Revolutionize Your Customer Loyalty</h3>
          <p className="text-slate-400 text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
            Switch from paper cards to a modern digital experience. Get real-time analytics, increase repeat visits, and delight your customers.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate('/register')} className="modern-button">Register Your Shop</button>
            <button onClick={() => navigate('/shopowner')} className="modern-outline !border-white/10 !bg-white/5 !text-white hover:!bg-white/10">Merchant Portal</button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            {[
              { label: 'Instant Setup', value: '< 5 Mins' },
              { label: 'Retention Boost', value: '85%+' },
              { label: 'Merchant Trust', value: '500+ Shops' },
              { label: 'Daily Scans', value: '10k+' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-black text-white mb-1">{stat.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-32 bg-white px-6 scroll-mt-24">
        <div className="max-w-4xl mx-auto">
          <div className="modern-card p-8 md:p-16 border-brand-primary/20 shadow-brand-primary/20/50">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-brand-secondary mb-4">Start Your Growth Journey</h3>
              <p className="text-slate-500">Fill out the form below and our team will get back to you within 24 hours.</p>
            </div>
            
            <form className="space-y-6" onSubmit={handleInquirySubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Owner Name</label>
                  <input 
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleInputChange}
                    type="text" 
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:border-brand-primary focus:bg-white transition-all" 
                    placeholder="Enter your name" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Shop Name</label>
                  <input 
                    name="shopName"
                    value={formData.shopName}
                    onChange={handleInputChange}
                    type="text" 
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:border-brand-primary focus:bg-white transition-all" 
                    placeholder="Your shop name" 
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Phone Number</label>
                  <input 
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    type="tel" 
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:border-brand-primary focus:bg-white transition-all" 
                    placeholder="+91 00000 00000" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Business Type</label>
                  <input 
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    type="text" 
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:border-brand-primary focus:bg-white transition-all" 
                    placeholder="e.g. Restaurant, Retail, Gym..." 
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Message (Optional)</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4" 
                  className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:border-brand-primary focus:bg-white transition-all" 
                  placeholder="Tell us about your requirements..."
                ></textarea>
              </div>
              <button 
                type="submit" 
                disabled={submitting}
                className={`modern-button w-full py-5 text-lg flex items-center justify-center gap-3 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {submitting ? 'Sending Inquiry...' : 'Submit Inquiry'}
                {!submitting && <ArrowRight size={20} />}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="flex items-center gap-2">
            <img src="/Sahyadri Tech Logo.png2.png" alt="Sahyadri Tech" className="h-8 w-auto object-contain" />
          </div>
          <div className="flex flex-wrap justify-center gap-8 text-slate-400 font-semibold text-sm">
            <button className="hover:text-brand-primary transition-colors">Privacy</button>
            <button className="hover:text-brand-primary transition-colors">Terms</button>
            <button className="hover:text-brand-primary transition-colors">Support</button>
            <button className="hover:text-brand-primary transition-colors">Contact</button>
          </div>
          <div className="text-slate-400 text-sm font-medium">
            © 2026 Sahyadri Tech. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
